import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { Globe, Loader2 } from "lucide-react";
import { detectRegion, describeWorkweek } from "@/lib/regionDetect";

/**
 * First-run region setup. Detects timezone / locale / workweek from
 * the browser and lets the user confirm (or tweak) before applying.
 *
 * Fires when `userPreferences.regionSetupAckedAt` is null. After the
 * user confirms, the server stamps the timestamp and the modal
 * never shows again. The fields here mirror the existing Settings
 * dialog — this is a focused "let's get the defaults right
 * up-front" surface so the user doesn't have to hunt through
 * settings on their first day.
 *
 * Runs *after* the workplace-surveillance `DisclosureModal`, so
 * the legal consent is dealt with first and the regional UX
 * preferences are layered on top.
 */
const TIMEZONES = [
  "UTC",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Asia/Muscat",
  "Africa/Cairo",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
];

export function RegionSetupDialog({
  open,
  onAck,
}: {
  open: boolean;
  onAck: () => void;
}) {
  const { locale: currentLocale, setLocale } = useI18n();
  const detected = useMemo(() => detectRegion(), []);
  const [timezone, setTimezone] = useState(detected.timezone);
  const [locale, setLocaleState] = useState<"en" | "ar">(detected.locale);
  const [workingDays, setWorkingDays] = useState(detected.workingDays);
  const [weekStartDay, setWeekStartDay] = useState(detected.weekStartDay);

  // If the dialog opens and we hadn't fired the detector yet (rare
  // race with the auth state load), re-pull. Also resync once when
  // the modal opens so the user always sees the current detection.
  useEffect(() => {
    if (!open) return;
    const d = detectRegion();
    setTimezone(d.timezone);
    setLocaleState(d.locale);
    setWorkingDays(d.workingDays);
    setWeekStartDay(d.weekStartDay);
  }, [open]);

  const ack = trpc.preferences.ackRegionSetup.useMutation({
    onSuccess: () => {
      // Also update the in-memory i18n locale so the rest of the app
      // re-renders in the chosen language immediately, not on next
      // refresh.
      if (locale !== currentLocale) setLocale(locale);
      onAck();
    },
  });

  // Workweek presets — most users will pick one of these two.
  const presets: Array<{
    label: string;
    workingDays: number;
    weekStartDay: number;
  }> = [
    {
      label: locale === "ar" ? "الأحد – الخميس" : "Sun – Thu",
      workingDays: 31,
      weekStartDay: 0,
    },
    {
      label: locale === "ar" ? "الإثنين – الجمعة" : "Mon – Fri",
      workingDays: 62,
      weekStartDay: 1,
    },
  ];

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        className="sm:max-w-lg max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-none max-sm:max-h-[92dvh] max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto"
      >
        <DialogTitle className="font-display text-xl flex items-center gap-2">
          <Globe className="h-5 w-5 text-coral" />
          {locale === "ar"
            ? "إعداد المنطقة والتفضيلات"
            : "Set up your region"}
        </DialogTitle>

        <p className="text-sm text-muted-foreground mt-1">
          {locale === "ar"
            ? "اكتشفنا هذه القيم من متصفّحك. أكدها أو عدّلها قبل البدء."
            : "We detected these from your browser. Confirm or adjust before continuing."}
        </p>

        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {locale === "ar" ? "اللغة" : "Language"}
            </Label>
            <div className="mt-2 inline-flex rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setLocaleState("en")}
                className={
                  "px-4 py-1.5 text-sm rounded-md transition-colors " +
                  (locale === "en"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground")
                }
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLocaleState("ar")}
                className={
                  "px-4 py-1.5 text-sm rounded-md transition-colors " +
                  (locale === "ar"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground")
                }
              >
                العربية
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {locale === "ar" ? "المنطقة الزمنية" : "Timezone"}
            </Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* If the detected timezone isn't in the curated
                    short list (e.g. Asia/Tokyo), inject it so the
                    user can still keep it. */}
                {!TIMEZONES.includes(timezone) && (
                  <SelectItem value={timezone}>{timezone}</SelectItem>
                )}
                {TIMEZONES.map(z => (
                  <SelectItem key={z} value={z}>
                    {z}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-muted-foreground mt-1">
              {locale === "ar" ? "مُكتشفة: " : "Detected: "}
              <span className="font-mono">{detected.timezone}</span>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {locale === "ar" ? "أيام العمل" : "Working days"}
            </Label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {presets.map(p => {
                const active =
                  p.workingDays === workingDays &&
                  p.weekStartDay === weekStartDay;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      setWorkingDays(p.workingDays);
                      setWeekStartDay(p.weekStartDay);
                    }}
                    className={
                      "px-3 py-1.5 text-sm rounded-lg border transition-colors " +
                      (active
                        ? "bg-coral text-white border-coral"
                        : "bg-card text-foreground border-border hover:bg-muted")
                    }
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
              {describeWorkweek(workingDays, locale)}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {locale === "ar"
              ? "يمكنك تغيير هذه القيم لاحقاً من الإعدادات."
              : "You can change these later in Settings."}
          </div>
          <Button
            onClick={() =>
              ack.mutate({
                timezone,
                locale,
                weekStartDay,
                workingDays,
              })
            }
            disabled={ack.isPending}
            className="bg-coral hover:bg-coral/90 text-white"
          >
            {ack.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin me-2" />
            ) : null}
            {locale === "ar" ? "تأكيد ومتابعة" : "Confirm & continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
